import { createFileRoute, Outlet, Link, useMatch } from '@tanstack/react-router'
import { useSessionUser } from '@/hooks/use-session'
import { useLingui } from '@lingui/react/macro'
import { UserIcon, CreditCardIcon, CodeIcon } from 'lucide-react'
import { TitleSection } from './-components/title-section'
import { NAVIGATION_ICONS } from './-components/shared/navigation-config'
import { AnimatedTabs } from '@/components/custom/animated-tabs'
import { SettingsLayoutPending } from './-components/app-layout-skeleton'
import { usePermissions } from '@/hooks/use-permissions'

export const Route = createFileRoute('/_app/account')({
  component: RouteComponent,
  pendingComponent: SettingsLayoutPending,
})

function RouteComponent() {
  const { t } = useLingui()
  const user = useSessionUser()
  const { isCompanyView } = usePermissions()
  // Any professional (self-registered or a company-registered employee) sees their own profile data, not
  // the company Settings layout (no Profile/Billing/API tabs — those stay owner-only). Keep the tab-gate a
  // pure applicant check so a company-view admin still doesn't get Billing/API.
  const isApplicant = user?.type === 'applicant'
  // Header label: a professional in their OWN profile reads "My profile data"; a company account OR an
  // admin/authorized employee acting AS the company (company view → /account shows the company profile)
  // reads "Settings" (Configuración).
  const showProfileData = isApplicant && !isCompanyView

  const settingsMenuItems = [
    { to: '/account', label: t`Profile`, icon: UserIcon },
    { to: '/account/billing', label: t`Billing`, icon: CreditCardIcon },
    { to: '/account/api', label: t`API`, icon: CodeIcon },
  ]

  const isBilling = useMatch({ from: '/_app/account/billing', shouldThrow: false })
  const isApi = useMatch({ from: '/_app/account/api', shouldThrow: false })
  const currentTab = isBilling ? 'billing' : isApi ? 'api' : 'account'

  return (
    <>
      <div className="flex h-full flex-col">
        <TitleSection
          icon={showProfileData ? NAVIGATION_ICONS.applicant : NAVIGATION_ICONS.settings}
          title={showProfileData ? t`My profile data` : t`Settings`}
        />
        {!isApplicant && (
          <div className="border-b">
            <AnimatedTabs
              tabs={settingsMenuItems.map((item) => {
                const Icon = item.icon
                return {
                  value: item.to.split('/').pop() || '',
                  children: (
                    <Link to={item.to} className="flex items-center gap-2 px-4 py-3">
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </Link>
                  ),
                }
              })}
              initialTabId={currentTab}
            />
          </div>
        )}
        {/* min-h-0 lets this scroll area shrink inside the flex column; without it the
            column overflows its h-full parent, the whole page scrolls too (the stray white
            band) and the z-40 title bar paints over the logo. Matches PageLayout.
            `relative` gives a positioning context so the hidden native <select> that Radix
            renders for form fields (position:absolute) anchors here and gets clipped, instead
            of anchoring to #root-page and stretching the document into a second scrollbar. */}
        <div className="scrollbar relative min-h-0 flex-1 overflow-y-scroll">
          <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
            <Outlet />
          </div>
        </div>
      </div>
    </>
  )
}
